import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  DescriptionManagement,
  ItemDescription,
  EditAnimals,
  ContentCards,
  ContentAnimals,
  ContentButton,
  ManagementAnimals,
} from "./styles";
import {
  IManagedAnimal,
  IManagementOptions,
  IManagementProps,
  RemoveAnimalByManagement,
} from "../../modules/management/interfaces";
import { ItemCardAnimalForManagement } from "../../modules/management/templates/create/styles";
import {
  IAnimal,
  IBatchOrFlock,
  SelectAnimal,
} from "../../modules/animals/interface";
import { ItemAnimal } from "../ItemAnimal";
import { CardOfFlocksOrBatchsForManagement } from "../CardOfFlocksOrBatchsForManagement";
import { AnimalWeightCard } from "../AnimalWeightCard";
import { updateManagement } from "../../services/management";
import { useNotification } from "../../hooks/useNotification";
import { useTranslate } from "../../hooks/useTranslate";
import {
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, ExpandLess } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useDialog } from "../../hooks/useDialog";

interface AboutManagementProps {
  editable?: boolean;
  management: IManagementProps;
  flocks?: IBatchOrFlock[];
  batchs?: IBatchOrFlock[];
  animals?: IAnimal[];
  removeAnimal: (data: RemoveAnimalByManagement) => Promise<void>;
}

const AboutManagement = ({
  editable = false,
  management,
  batchs = [],
  animals = [],
  flocks = [],
  removeAnimal,
}: AboutManagementProps) => {
  const dialog = useDialog();
  const navigate = useNavigate();
  const { successNotification } = useNotification();
  const snackbar = useSnackbar();
  const { translate } = useTranslate();

  const [loading, setLoading] = useState(false);
  const [option, setOption] = useState<IManagementOptions>("");
  const [newAnimals, setNewAnimals] = useState<IAnimal[]>([]);
  const [openFormWeight, setOpenFormWeight] = useState(false);
  const [selectedBatchs, setSelectedBatchs] = useState<string[]>([]);
  const [selectedFlocks, setSelectedFlocks] = useState<string[]>([]);

  const handleRemoveAnimal = async (animal: IManagedAnimal) => {
    await removeAnimal({
      idAnimal: animal.id,
      idManagement: management.id,
    });
  };

  const handleClickAnimal = ({ value, id }: SelectAnimal) => {
    if (value) {
      const animal = animals.find((animal) => animal.id === id);
      setNewAnimals((oldState) => [...oldState, animal!]);
      return;
    }

    const selectedAnimalsFiltered = newAnimals.filter(
      (animal) => animal.id !== id
    );
    setNewAnimals(selectedAnimalsFiltered);
  };

  const handleClickBatch = (id: string) => {
    let batchs = selectedBatchs;
    const index = selectedBatchs.indexOf(id);

    if (index >= 0) {
      batchs = selectedBatchs.filter((batch) => batch !== id);
    } else {
      batchs.push(id);
    }

    setSelectedBatchs([...batchs]);
    const animalsByBatch = animals.filter((animal) =>
      batchs.includes(animal.batch?.id || "")
    );
    setNewAnimals(animalsByBatch);
  };

  const handleClickFlock = (id: string) => {
    let flocks = selectedFlocks;
    const index = selectedFlocks.indexOf(id);

    if (index >= 0) {
      flocks = selectedFlocks.filter((batch) => batch !== id);
    } else {
      flocks.push(id);
    }

    setSelectedFlocks([...flocks]);
    const animalsByFlock = animals.filter((animal) =>
      flocks.includes(animal.flock?.id || "")
    );
    setNewAnimals(animalsByFlock);
  };

  const handleSubmit = async (newAnimals: IManagedAnimal[]) => {
    setLoading(true);
    const allAnimals = management.attributes.animals;

    newAnimals.forEach((animal) => {
      const index = allAnimals.findIndex(
        (oldAnimal) => oldAnimal.id === animal.id
      );

      if (index >= 0) {
        allAnimals[index] = animal;
      } else {
        allAnimals.push(animal);
      }
    });

    await updateManagement({ id: management.id, data: allAnimals });
    successNotification(translate("management.update_success_message"));
    setLoading(false);
    navigate("/managements");
  };

  const [expanded, setExpanded] = useState<string | null>(
    () => management.attributes.animals?.[0]?.id ?? null
  );

  return (
    <>
      {/* <ModalConfirm
        visible={openModal}
        onCancel={() => {
          setOpenModal(false);
        }}
        onOk={handleRemoveAnimal}
        title={translate("management.title_modal_confirm")}
        subtitle={translate("management.subtitle_modal_confirm")}
      /> */}

      <Paper>
        <Stack sx={{ p: 2 }} spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Typography
              color="primary"
              sx={{ fontWeight: "bold", fontSize: 20 }}
            >
              {translate("management.details")}
            </Typography>
            {!editable && (
              <IconButton
                color="primary"
                component={Link}
                to={`/managements/edit/${management.id}`}
              >
                <Edit />
              </IconButton>
            )}
          </Stack>
          <DescriptionManagement>
            <ItemDescription>
              <p>{translate("management.name_management")}</p>
              <span>{management.nameManagement}</span>
            </ItemDescription>
            <ItemDescription>
              <p>{translate("management.item_date")}</p>
              <span>{management.createdAtFormatted}</span>
            </ItemDescription>
            <ItemDescription>
              <p>{translate("management.item_author")}</p>
              <span>{management.author}</span>
            </ItemDescription>
            <ItemDescription>
              <p>{translate("management.item_activities")}</p>
              <span>{management.attributes.activityFormatted!.join(", ")}</span>
            </ItemDescription>
          </DescriptionManagement>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              color="primary"
              sx={{ fontSize: 20, fontWeight: "bold" }}
            >
              {translate("management.title_header_management")}
            </Typography>
            <Chip
              color="primary"
              size="small"
              label={management.attributes.animals.length}
            />
          </Stack>
          <Stack spacing={1}>
            {management.attributes.animals.map((animal) => (
              <Paper
                component={Stack}
                spacing={1}
                sx={{ bgcolor: "grey.100", p: 2 }}
                elevation={0}
                key={animal.id}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ cursor: "pointer", color: "primary.main" }}
                >
                  <Typography
                    component={"a"}
                    onClick={() => {
                      setExpanded(() => {
                        if (expanded === animal.id) return null;
                        return animal.id;
                      });
                    }}
                    sx={{ color: "inherit", fontWeight: "bold", flex: 1 }}
                  >
                    {animal.name}
                  </Typography>
                  {editable && (
                    <Tooltip title={translate("management.remove_animal")}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          dialog.open({
                            title: translate("management.title_modal_confirm"),
                            message: translate(
                              "management.subtitle_modal_confirm"
                            ),
                            buttons: [
                              {
                                label: translate("global.cancel"),
                                options: {
                                  color: "inherit",
                                },
                              },
                              {
                                label: translate("global.delete"),
                                async callback() {
                                  handleRemoveAnimal(animal);
                                },
                                options: {
                                  color: "error",
                                  sx: { fontWeight: "bold" },
                                },
                              },
                            ],
                          });
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => {
                      setExpanded(() => {
                        if (expanded === animal.id) return null;
                        return animal.id;
                      });
                    }}
                  >
                    <ExpandLess
                      sx={(theme) => ({
                        transition: theme.transitions.create("transform"),
                        transform: `rotate(${
                          expanded === animal.id ? 180 : 0
                        }deg)`,
                      })}
                    />
                  </IconButton>
                </Stack>
                <Collapse in={expanded === animal.id} unmountOnExit>
                  {(management.attributes.activity ?? []).map(
                    (activity: string, index) => (
                      <Stack
                        spacing={2}
                        key={activity}
                        justifyContent="space-between"
                        alignItems="center"
                        direction="row"
                      >
                        <Typography
                          sx={{ color: "grey.500", flex: 1, fontSize: 14 }}
                        >
                          {management.attributes?.activityFormatted?.[index]}
                        </Typography>
                        <Typography
                          sx={{
                            color: "grey.600",
                            fontWeight: "bold",
                            fontSize: 14,
                          }}
                        >
                          {String((animal.result as any)?.[activity])}
                        </Typography>
                      </Stack>
                    )
                  )}
                  {/* <Accordion
              accordion
              expandIconPosition="end"
              expandIcon={({ isActive }) => (
                <FiChevronDown
                  size={20}
                  style={{ transform: `rotate(${isActive ? 180 : 0}deg)` }}
                />
              )}
            >
              {management.attributes.animals.map((animal) => (
                <ItemPanel header={animal.name} key={animal.id}>
                  <DescribeItemPanel>
                    <p>{translate("management.weight")}</p>
                    <span>{animal.result.pesagem} kg</span>
                  </DescribeItemPanel>
                  <DescribeItemPanel>
                    <p>{translate("management.description")}</p>
                    <span>{animal.result.diagnosticoDePrenhez}</span>
                  </DescribeItemPanel>
                  <RemoveAnimal
                    type="button"
                    onClick={() => {
                      setRemoveAnimalId(animal.id);
                      setOpenModal(true);
                    }}
                  >
                    {translate("management.remove_animal")}
                  </RemoveAnimal>
                </ItemPanel>
              ))}
            </Accordion> */}
                </Collapse>
              </Paper>
            ))}
          </Stack>
          {editable && (
            <Stack spacing={2}>
              {!openFormWeight && (
                <Stack spacing={2}>
                  {!option && (
                    <Stack direction="row" spacing={2}>
                      <Paper
                        component={Button}
                        startIcon={<Add />}
                        sx={{
                          bgcolor: "grey.100",
                          p: 2,
                          flex: 1,
                          textTransform: "capitalize",
                        }}
                        elevation={0}
                        onClick={() => {
                          if (animals.length > 0) {
                            setOption("animals");
                          } else {
                            snackbar.enqueueSnackbar(
                              translate("management.any_animal"),
                              {
                                variant: "error",
                              }
                            );
                          }
                        }}
                      >
                        {translate("management.select_option_1")}
                      </Paper>
                      <Paper
                        component={Button}
                        startIcon={<Add />}
                        sx={{
                          bgcolor: "grey.100",
                          p: 2,
                          flex: 1,
                          textTransform: "capitalize",
                        }}
                        elevation={0}
                        onClick={() => {
                          if (batchs.length > 0) {
                            setOption("batch");
                          } else {
                            snackbar.enqueueSnackbar(
                              translate("management.any_batch"),
                              {
                                variant: "error",
                              }
                            );
                          }
                        }}
                      >
                        {translate("management.select_option_2")}
                      </Paper>
                      <Paper
                        component={Button}
                        startIcon={<Add />}
                        sx={{
                          bgcolor: "grey.100",
                          p: 2,
                          flex: 1,
                          textTransform: "capitalize",
                        }}
                        elevation={0}
                        onClick={() => {
                          if (flocks.length > 0) {
                            setOption("flock");
                          } else {
                            snackbar.enqueueSnackbar(
                              translate("management.any_flock"),
                              {
                                variant: "error",
                              }
                            );
                          }
                        }}
                      >
                        {translate("management.select_option_3")}
                      </Paper>
                      {animals.length === 0 &&
                        batchs.length === 0 &&
                        flocks.length === 0 && (
                          <span>
                            {translate("management.without_items_management")}
                          </span>
                        )}
                    </Stack>
                  )}

                  {option === "animals" && (
                    <ContentAnimals>
                      {animals.map((animal) => (
                        <ItemAnimal
                          key={animal.id}
                          animal={animal}
                          editable
                          disabled
                          handleClick={handleClickAnimal}
                        />
                      ))}
                    </ContentAnimals>
                  )}

                  {option === "batch" && (
                    <ContentAnimals>
                      {batchs.map((batch) => (
                        <CardOfFlocksOrBatchsForManagement
                          id={batch.id}
                          key={batch.id}
                          cb={() => {
                            handleClickBatch(batch.id);
                          }}
                          name={batch.name}
                          count={batch.count}
                          checked={selectedBatchs.includes(batch.id)}
                        />
                      ))}
                    </ContentAnimals>
                  )}

                  {option === "flock" && (
                    <ContentAnimals>
                      {flocks.map((flock) => (
                        <CardOfFlocksOrBatchsForManagement
                          id={flock.id}
                          key={flock.id}
                          name={flock.name}
                          count={flock.count}
                          cb={() => {
                            handleClickFlock(flock.id);
                          }}
                          checked={selectedFlocks.includes(flock.id)}
                        />
                      ))}
                    </ContentAnimals>
                  )}
                  <Stack alignItems="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setOpenFormWeight(true);
                      }}
                      disabled={newAnimals.length === 0}
                      type="submit"
                    >
                      {translate("management.label_btn_step_1")}
                    </Button>
                  </Stack>
                </Stack>
              )}

              {openFormWeight && (
                <ManagementAnimals>
                  <AnimalWeightCard
                    animals={newAnimals}
                    cb={async (animals) => {
                      await handleSubmit(animals);
                    }}
                    loading={loading}
                  />
                </ManagementAnimals>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>
    </>
  );
};

export { AboutManagement };
